import {computed, observable, override, makeObservable} from "mobx";
import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {ODDS_TYPES} from "../../../core/components/odds/components/odds-consts";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {PredictionsWidgetConfig} from "../predictions-widget.config";
import {GAME_STATUSES} from "../../../core/app/consts";
import {COMPONENT_STATES} from "../../../core/consts";
import {ListItemViewModel} from "../../../infrastructure/models/list-item.view-model";
import {PredictionWidgetCardsModel} from "./cards/prediction-widget.cards.model";
import {PREDICTION_WIDGET} from "../predictions-widget.consts";
import {PropositionBetsDetailsDisplayModel} from "../../../core/models/proposition-bets-details/proposition-bets.details.display-model";
import {PredictionTabViewModel} from "./prediction-tab-view-model";
import type {PlayerBetPredictionViewModel} from "../../../domain/full-game/models/player-bet-prediction/player-bet-prediction.view-model";

export class PredictionsWidgetState extends BaseModuleState {
  @observable tabIndex: Number = 0;
  @observable popupDisplay: PropositionBetsDetailsDisplayModel;
  @observable predictions: Array<PredictionTabViewModel>
  @observable playersBetsPredictions: Array<PlayerBetPredictionViewModel>
  @observable currentContext: PredictionWidgetCardsModel

  static injectables = [...BaseMobxState.injectables,
    NavigationManager,
    AppStore,
    LoggerUtils,
    DatetimeUtils,
    DeviceViewModel,
    TermsUtils,
    PredictionsWidgetConfig
  ];

  constructor(
    navigationManager: NavigationManager,
    appStore: AppStore,
    logger: LoggerUtils,
    datetimeUtils: DatetimeUtils,
    device: DeviceViewModel,
    termsUtils: TermsUtils,
    config: PredictionsWidgetConfig,
  ) {
    super(navigationManager, appStore, logger, datetimeUtils, device, termsUtils, config);

    this.popupDisplay = new PropositionBetsDetailsDisplayModel();

    makeObservable(this);
  }

  @override get isPopulated(): Boolean {
    const hasPrediction: Boolean = this.dataSource?.game?.predictions?.length > 0
    const hasPlayerPrediction: Boolean = this.dataSource?.game?.playersBetsPredictions?.length > 0;

    return this.componentState !== COMPONENT_STATES.loaded || hasPrediction || hasPlayerPrediction;
  }

  @computed get selectedOddsType(): ODDS_TYPES {
    return this.appState.currentOddsType;
  }

  @computed get tabs(): Array<PredictionTabViewModel> {
    const tabs: Array<PredictionTabViewModel> = [];
    const mainTab: PredictionTabViewModel = new PredictionTabViewModel({index: PREDICTION_WIDGET.TABS.main, name: this.terms.WEB_MAIN_FILTER_PROPS});
    const propsTab: PredictionTabViewModel = new PredictionTabViewModel({index: PREDICTION_WIDGET.TABS.props, name: this.terms.WEB_PROPS_FILTER_PROPS});

    this.hasMainTab && tabs.push(mainTab);
    this.hasPropsTabs && tabs.push(propsTab);

    return tabs;
  }

  @computed get selectedTab(): ListItemViewModel {
    const selectedTab: ListItemViewModel = this.tabs.find((tab: ListItemViewModel): Boolean => tab.index === this.tabIndex);

    return selectedTab;
  }

  @computed get hasMainTab(): Boolean {
    return this.predictions?.length > 0;
  }

  @computed get hasPropsTabs(): Boolean {
    return this.playersBetsPredictions?.length > 0;
  }

  @computed get isPredictionTabsVisible(): Boolean {
    return this.tabIndex === PREDICTION_WIDGET.TABS.main;
  }

  @computed get isPropsTabsVisible(): Boolean {
    return this.tabIndex === PREDICTION_WIDGET.TABS.props;
  }

  @computed get shouldRenderTabs(): Boolean {
    return this.tabs.length > 1
  }

  @computed get date(): Date {
    return this.dataSource?.game.startTime;
  }

  @computed get showOdds() {
    const showOdds = this.appState.showOdds || this.appState.showSecondaryOdds;

    return showOdds;
  }

  get gameId(): Number {
    return this.dataSource?.game.id;
  }

  get statusGroup(): GAME_STATUSES {
    return this.dataSource?.game.statusGroup;
  }

  get competitionId(): Number {
    return this.dataSource?.game.competition.id;
  }

  get shouldRenderSponsorship() {
    const shouldRenderSponsorship: Boolean = this.appState.settings.bootstrapSettings.showSponsorship || this.showOdds;

    return shouldRenderSponsorship
  }

  @computed get isContextualMode(): Boolean {
    const isContextualMode: Boolean = this.appSettings.isContextualMode;

    return isContextualMode;
  }

  @computed get isContextualUsed(): Boolean {
    const isContextualUsed: Boolean = this.dataSource?.game.apiModel.summary?.isContextualUse;

    return isContextualUsed;
  }

  get showDisclaimer(): Boolean {
    return this.appSettings.bootstrapSettings.showDisclaimer;
  }

  get sportTypeId(): Number {
    return this.dataSource?.game?.sport.id;
  }

  @computed get isVisible(): Boolean {
    const isVisible: Boolean = this.popupDisplay?.athleteId > 0 && this.popupDisplay?.lineTypeId > 0 && this.popupDisplay?.gameId > 0;

    return isVisible;
  }
}
